import React from "react";
import { useTranslation } from "react-i18next";

import { DateTimeInput } from "@common/dateTimeInput";
import { TextInput } from "@common/textInput";
import { ListSelect } from "@common/listSelect";
import { RichTextInput } from "@common/richTextInput";
import { Props } from "./types";
import styles from "./styles.scss";

function ListCreationForm({ formData, lists, error, editForm }: Props) {
  const { t } = useTranslation();

  return (
    <div className={styles.listCreationForm}>
      <div className={styles.group}>
        <h3 className={styles.title}>{t("listCreation.nameTitle")}</h3>
        <div className={styles.textInput}>
          <TextInput
            value={formData.name}
            placeholder={t("listCreation.namePlaceholder")}
            input={(name) => editForm({ ...formData, name })}
            isInvalid={error.duplicateName || error.emptyName}
            autoFocus
          />
        </div>
      </div>
      <div className={styles.dateGroup}>
        <div className={styles.row}>
          <h3 className={styles.title}>{t("listCreation.startDateTitle")}</h3>
          <div className={styles.dateTimeInput}>
            <DateTimeInput
              dateTime={formData.startDate}
              placeholder={t("listCreation.startDatePlaceholder")}
              minDate={
                formData.parentList?.startDate
                  ? {
                      year: new Date(
                        formData.parentList.startDate,
                      ).getFullYear(),
                      month: new Date(
                        formData.parentList?.startDate,
                      ).getMonth(),
                      day: new Date(formData.parentList?.startDate).getDate(),
                    }
                  : !!formData.minStartDate
                    ? {
                        year: new Date(formData.minStartDate).getFullYear(),
                        month: new Date(formData.minStartDate).getMonth(),
                        day: new Date(formData.minStartDate).getDate(),
                      }
                    : undefined
              }
              input={(startDate) => {
                const date = new Date(startDate);

                editForm({
                  ...formData,
                  startDate,
                  endDate: !formData.endDate
                    ? new Date(
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate(),
                      ).toISOString()
                    : formData.endDate,
                });
              }}
              clear={() => editForm({ ...formData, startDate: "" })}
              isInvalid={error.startDateOutOfBound || error.invalidDateRange}
            />
          </div>
        </div>
        <div className={styles.row}>
          <h3 className={styles.title}>{t("listCreation.endDateTitle")}</h3>
          <div className={styles.dateTimeInput}>
            <DateTimeInput
              dateTime={formData.endDate}
              placeholder={t("listCreation.endDatePlaceholder")}
              input={(endDate) => {
                const date = new Date(endDate);

                editForm({
                  ...formData,
                  endDate,
                  startDate: !formData.startDate
                    ? new Date(
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate(),
                      ).toISOString()
                    : formData.startDate,
                });
              }}
              clear={() => editForm({ ...formData, endDate: "" })}
              isInvalid={error.invalidDateRange}
            />
          </div>
        </div>
      </div>
      <div className={styles.group}>
        <h3 className={styles.title}>{t("listCreation.parentListTitle")}</h3>
        <div className={styles.listSelect}>
          <ListSelect
            lists={lists}
            selectedList={formData.parentList}
            selectList={(list) =>
              editForm({ ...formData, parentList: list, startDate: "" })
            }
          />
        </div>
      </div>
      <div className={styles.group}>
        <h3 className={styles.title}>{t("listCreation.descriptionTitle")}</h3>
        <RichTextInput
          value={formData.description}
          maxLength={5000}
          placeholder={t("listCreation.descriptionPlaceholder")}
          input={(description) => editForm({ ...formData, description })}
        />
      </div>
    </div>
  );
}

export { ListCreationForm };
